import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RemoveFormula } from "building-types/options/formula";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { GenerateRoofDormerOptions, RoofDormer } from "building-types/roofs/roofGenerationTypes";
import type {
  DormerInputSettings,
  GetMergedSettingsForDormer,
  GetMergedSettingsForWindow,
} from "building-types/roofs/roofInputSettings";
import type { RoofDormerPlacement } from "building-types/roofs/roofSystemTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { WallLayer } from "building-types/walls/wallTypes";
import { type WindowHomeSettings } from "building-types/windows/windowMergeTypes";
import type { RWTable, Table } from "common/data/table";
import shallowCompare from "common/utils/shallowCompare";
import { getMergedGableWindowSettings, getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { getWindowCatalog } from "doc/elements/window/windowCatalog";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { buildRoofDormers } from "../elements/roofs/roofDormers";
import { wallLayersAddFromRoofs, wallLayerTable } from "../elements/walls/wallLayers";
import { memoAsync } from "../utils/memo";

function select({
  roofSystems,
  roofSystemClipContexts,
  mirror,
  windowPlacements,
  windowUnits,
  windowShutters,
  optionChoices,
  levels,
  settings,
  wallLayers,
  doc: {
    assets,
    dormers,
    entitySettings,
    optionSets,
    instanceOptionSets,
    roofSystems: initialRoofSystems,
    roofs: initialRoofs,
    settings: buildingDocSettings,
    profiles,
  },
  settingsSets: activeSettingsSets,
  enabledFeatures,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    {
      roofSettings: settings.roof,
      dormerSettings: settings.dormer,
      roofFramingSettings: settings.roofFraming,
      dormers,
      initialRoofSystems,
      initialRoofs,
    },
    definitions,
    optionChoices,
  );

  const roofSettings = evalFormulas(settings.roof, (v) => resolvedOptionChoices[v]) as RemoveFormula<RoofSettings>;
  const dormerSettings = evalFormulas(settings.dormer, (v) => resolvedOptionChoices[v]) as DormerSettings;
  const roofFramingSettings = evalFormulas(
    settings.roofFraming,
    (v) => resolvedOptionChoices[v],
  ) as RoofFramingSettings;

  return {
    roofSystems,
    roofSystemClipContexts,
    mirror,
    windowsFromBuilding: windowPlacements,
    windowUnitsFromBuilding: windowUnits,
    windowShuttersFromBuilding: windowShutters,
    optionChoices: resolvedOptionChoices,
    levels,
    buildingDocSettings,
    dormers,
    entitySettings,
    dormerSettings,
    roofSettings,
    roofFramingSettings,
    activeSettingsSets,
    roofPlanSettings: settings.planset,
    wallSettings: settings.wall,
    exteriorTrimSettings: settings.exteriorTrim,
    propCatalog: settings.props,
    assets,
    windowMaterialSettings: settings.windowMaterial,
    windowSettings: settings.window,
    windowShutterSettings: settings.windowShutters,
    windowMullionSettings: settings.windowMullions,
    exteriorWindowCasingSettings: settings.exteriorWindowCasing,
    interiorWindowCasingSettings: settings.interiorWindowCasing,
    windowWrapping: settings.windowWrapping,
    wainscotingSettings: settings.wainscoting,
    awningSettings: settings.awning,
    profileSource: {
      settingsToCatalogs: enabledFeatures.settingsToCatalogs,
      profiles,
      profileSettings: settings.profile,
    },
    windowCatalogSettings: settings.windowCatalog,
    windowFramingSettings: settings.windowFraming,
    wallLayers,
    enabledFeatures,
  };
}

function compare(a: ReturnType<typeof select>, b: ReturnType<typeof select>) {
  if (!a || !b) {
    return false;
  }

  for (const k in a) {
    if (!shallowCompare(a[k], b[k])) {
      // This fixes BDG-292. The dormer object can shallowly change while its the properties
      // remaining the same. When the dormer that happens the roof system does not update as
      // it normally does and the top of the roof goes missing.
      if (k !== "dormers") {
        return false;
      }
    }
  }
  return true;
}

async function generate({
  roofSystems,
  roofSystemClipContexts,
  mirror,
  windowsFromBuilding,
  windowUnitsFromBuilding,
  windowShuttersFromBuilding,
  optionChoices,
  levels,
  buildingDocSettings,
  dormers,
  entitySettings,
  dormerSettings,
  roofSettings,
  roofFramingSettings,
  activeSettingsSets,
  roofPlanSettings,
  wallSettings,
  exteriorTrimSettings,
  propCatalog,
  assets,
  windowMaterialSettings,
  windowSettings,
  windowShutterSettings,
  windowMullionSettings,
  exteriorWindowCasingSettings,
  interiorWindowCasingSettings,
  windowWrapping,
  wainscotingSettings,
  awningSettings,
  profileSource,
  windowCatalogSettings,
  windowFramingSettings,
  wallLayers,
  enabledFeatures,
}: ReturnType<typeof select>): Promise<
  Pick<
    Building,
    | "roofSystems"
    | "roofSystemClipContexts"
    | "dormers"
    | "windowPlacements"
    | "windowUnits"
    | "windowShutters"
    | "wallLayers"
    | "issues"
  >
> {
  const issues: BuildingGenerationIssue[] = [];
  const dormersFiltered = evalFormulas(dormers, (v) => optionChoices[v]) as Table<RoofDormerPlacement>;
  const windowPlacements = { ...windowsFromBuilding };
  const windowUnits = { ...windowUnitsFromBuilding };
  const windowShutters = { ...windowShuttersFromBuilding };

  let generatedDormers: RWTable<RoofDormer> = {};

  if (!roofSystems) {
    return {
      roofSystems,
      roofSystemClipContexts,
      dormers: generatedDormers,
      windowPlacements: windowPlacements,
      windowUnits,
      windowShutters,
      wallLayers,
      issues,
    };
  }

  const windowCatalog = getWindowCatalog(windowCatalogSettings);

  const windowHomeSettings: WindowHomeSettings = {
    windowShutters: windowShutterSettings,
    exteriorWindowCasing: exteriorWindowCasingSettings,
    interiorWindowCasing: interiorWindowCasingSettings,
    awning: awningSettings,
    windowMullions: windowMullionSettings,
    windowFraming: windowFramingSettings,
    windowWrapping,
    window: windowSettings,
    windowMaterial: windowMaterialSettings,
  };

  const getMergedWindowSettingsForDormer: GetMergedSettingsForDormer = (window, descriptor) =>
    getMergedGableWindowSettings({
      window,
      descriptor,
      homeSettings: windowHomeSettings,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings: buildingDocSettings,
    });
  const getMergedSettingsForWindow: GetMergedSettingsForWindow = (window, descriptor) =>
    getMergedWindowSettings({
      window,
      descriptor,
      homeSettings: windowHomeSettings,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings: buildingDocSettings,
    });

  const dormerInputSettings: DormerInputSettings = {
    roofSettings,
    roofFramingSettings,
    dormerSettings,
    roofPlanSettings,
    wallSettings,
    exteriorTrimSettings,
    propCatalog,
    assets,
    wainscotingSettings,
    profileSource,
    roofElevationPlanSettings: roofPlanSettings,
    getMergedSettingsForWindow,
    getMergedWindowSettingsForDormer,
  };

  const newRoofSystems: RWTable<GeneratedRoofSystem> = { ...roofSystems };
  const newRoofSystemClipContexts: RWTable<MultiGableRoofClipContext> = { ...roofSystemClipContexts };
  let newWallLayers: WallLayer[] = [];

  for (const roofSystemId in newRoofSystems) {
    const { roofs, levelId } = newRoofSystems[roofSystemId];
    const clipContext = newRoofSystemClipContexts[levelId];

    const generatedDormerRoofVolumes: RWTable<GeneratedRoofVolumeLegacy> = {};
    const generatedDormerRoofs: RWTable<GeneratedRoof> = {};

    let subGableOpts: GenerateRoofDormerOptions = {
      dormerInputSettings,
      clipContext,
      level: levels[levelId],
      mirror,
      addRoofWallFaces: !enabledFeatures.wallPlanes,
      windows: windowPlacements,
      windowUnits,
      windowShutters,
      windowCatalog,
      issues,
      dormerPlacements: [],
    };
    let startDormerGenIndex = Object.values(roofs).length + 1;

    for (const roofId in roofs) {
      const hostSubRoof = roofs[roofId];

      const filteredDormerPlacements: RoofDormerPlacement[] = Object.values(dormersFiltered).filter(
        (x) => x.parentId === hostSubRoof.id && !x.disabled,
      );

      const {
        roofPlanSettings: finalRoofPlanSettings,
        roofElevationPlanSettings: finalRoofElevationPlanSettings,
        roofSettings: finalRoofSettings,
        roofFramingSettings: finalRoofFramingSettings,
      } = hostSubRoof;

      subGableOpts = {
        ...subGableOpts,
        dormerInputSettings: {
          ...subGableOpts.dormerInputSettings,
          roofPlanSettings: finalRoofPlanSettings,
          roofElevationPlanSettings: finalRoofElevationPlanSettings,
          roofSettings: finalRoofSettings,
          roofFramingSettings: finalRoofFramingSettings,
        },
        dormerPlacements: filteredDormerPlacements,
      };

      const entityHandleForSelectingReportingUnit: DocEntityHandle = { type: "ROOF", id: roofId };

      const { dormers: builtRoofDormers, index } = await buildRoofDormers(
        startDormerGenIndex,
        roofSystemId,
        subGableOpts,
        hostSubRoof,
        generatedDormerRoofVolumes,
        generatedDormerRoofs,
        entitySettings,
        activeSettingsSets,
        optionChoices,
        buildingDocSettings,
        entityHandleForSelectingReportingUnit,
      );
      startDormerGenIndex = index;
      generatedDormers = { ...generatedDormers, ...builtRoofDormers };
    }

    newRoofSystems[roofSystemId] = {
      ...newRoofSystems[roofSystemId],
      roofVolumes: { ...newRoofSystems[roofSystemId].roofVolumes, ...generatedDormerRoofVolumes },
      roofs: { ...newRoofSystems[roofSystemId].roofs, ...generatedDormerRoofs },
    };

    newWallLayers = wallLayersAddFromRoofs(newWallLayers, generatedDormerRoofs);

    newRoofSystemClipContexts[roofSystemId] = clipContext;
  }

  const newWallLayersTable = newWallLayers.reduce(wallLayerTable, { ...wallLayers });

  return {
    roofSystems: newRoofSystems,
    roofSystemClipContexts: newRoofSystemClipContexts,
    dormers: generatedDormers,
    issues,
    windowPlacements,
    windowUnits,
    windowShutters,
    wallLayers: newWallLayersTable,
  };
}

export default memoAsync(select, generate, compare);
